import { useState, useEffect } from 'react'
import Button from '../../../components/common/Button/Button'
import { getHolidayTypes, formatDateForInput } from '../../../services/holidaysService'

/**
 * HolidayModal Component
 * Modal form for creating/editing holidays
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close modal callback
 * @param {function} onSubmit - Submit form callback
 * @param {object} holiday - Existing holiday data (for edit mode)
 * @param {boolean} isLoading - Whether form is submitting
 */
const HolidayModal = ({ isOpen, onClose, onSubmit, holiday = null, isLoading = false }) => {
  const types = getHolidayTypes()
  const maxNameLength = 100

  // Form state
  const [name, setName] = useState('')
  const [date, setDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [type, setType] = useState('National')
  const [error, setError] = useState('')

  // Load existing holiday data in edit mode
  useEffect(() => {
    if (holiday) {
      setName(holiday.name || '')
      setDate(formatDateForInput(holiday.date) || '')
      setEndDate(holiday.endDate ? formatDateForInput(holiday.endDate) : '')
      setType(holiday.type || 'National')
    } else {
      // Reset form in create mode
      resetForm()
    }
  }, [holiday, isOpen])

  const resetForm = () => {
    setName('')
    setDate('')
    setEndDate('')
    setType('National')
    setError('')
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    // Validate
    if (!name.trim()) {
      setError('Holiday name is required')
      return
    }

    if (name.length > maxNameLength) {
      setError(`Holiday name cannot exceed ${maxNameLength} characters`)
      return
    }

    if (!date) {
      setError('Start date is required')
      return
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateRegex.test(date)) {
      setError('Invalid date format')
      return
    }

    // Validate end date if provided
    if (endDate) {
      if (!dateRegex.test(endDate)) {
        setError('Invalid end date format')
        return
      }

      if (endDate < date) {
        setError('End date must be on or after start date')
        return
      }
    }

    // Submit
    const formData = {
      name: name.trim(),
      date: date,
      type: type
    }

    // Add endDate if provided
    if (endDate) {
      formData.endDate = endDate
    }

    onSubmit(formData)
  }

  const handleClose = () => {
    if (!isLoading) {
      resetForm()
      onClose()
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('psyber-modal-overlay')) {
      handleClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="psyber-modal-overlay" onClick={handleBackdropClick}>
      <div className="psyber-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="psyber-modal-header">
          <h3 style={{
            margin: 0,
            fontSize: 'var(--psyber-font-size-2xl)',
            color: 'var(--psyber-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <i className="fas fa-calendar-plus"></i>
            {holiday ? 'Edit Holiday' : 'Add New Holiday'}
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--psyber-text-secondary)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '2rem',
              height: '2rem',
              borderRadius: 'var(--psyber-radius-sm)',
              transition: 'var(--psyber-transition-fast)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.background = 'var(--psyber-card-hover)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none'
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          {/* Error Display */}
          {error && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--psyber-error-bg)',
              border: '2px solid var(--psyber-error)',
              borderRadius: 'var(--psyber-radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-exclamation-circle" style={{ fontSize: '1.5rem', color: 'var(--psyber-error)' }}></i>
              <span style={{ color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</span>
            </div>
          )}

          {/* Holiday Name Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="name"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-text"></i> Holiday Name *
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., New Year's Day"
              disabled={isLoading}
              maxLength={maxNameLength}
              className="psyber-input"
              style={{ width: '100%' }}
              required
            />
            <div style={{
              marginTop: '0.25rem',
              fontSize: 'var(--psyber-font-size-xs)',
              color: name.length > maxNameLength * 0.9 ? 'var(--psyber-warning)' : 'var(--psyber-text-muted)',
              textAlign: 'right'
            }}>
              {name.length}/{maxNameLength} characters
            </div>
          </div>

          {/* Start Date Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="date"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-calendar"></i> Start Date *
            </label>
            <input
              type="date"
              id="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={isLoading}
              className="psyber-input"
              style={{ width: '100%' }}
              required
            />
          </div>

          {/* End Date Field (Optional) */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="endDate"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-calendar-check"></i> End Date (Optional)
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={isLoading}
              className="psyber-input"
              style={{ width: '100%' }}
              placeholder="Leave empty for single-day holiday"
            />
            <div style={{
              marginTop: '0.25rem',
              fontSize: 'var(--psyber-font-size-xs)',
              color: 'var(--psyber-text-muted)'
            }}>
              Leave empty for single-day holiday
            </div>
          </div>

          {/* Type Dropdown */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="type"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-tag"></i> Type *
            </label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              disabled={isLoading}
              className="psyber-input"
              style={{
                width: '100%',
                cursor: 'pointer'
              }}
              required
            >
              {types.map(t => (
                <option key={t} value={t}>{t} Holiday</option>
              ))}
            </select>
          </div>

          {/* Help Text */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '0.75rem',
            background: 'var(--psyber-info-bg)',
            border: '1px solid var(--psyber-info)',
            borderRadius: 'var(--psyber-radius-md)',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <i className="fas fa-info-circle" style={{ color: 'var(--psyber-info)', marginRight: '0.5rem' }}></i>
            <strong>Tip:</strong> Choose "National" for public holidays, "Company" for organization-specific days, and "Optional" for flexible holidays.
          </div>

          {/* Modal Footer */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            paddingTop: '1rem',
            borderTop: '1px solid var(--psyber-card-border)'
          }}>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={holiday ? 'fas fa-save' : 'fas fa-plus'}
              loading={isLoading}
              disabled={isLoading || !name.trim() || !date}
            >
              {isLoading ? 'Saving...' : (holiday ? 'Update Holiday' : 'Add Holiday')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default HolidayModal
